import type { ControlPlaneOperationalProjection } from '@internal/spassu-code-contract';
import { Box, Button, Collapse, makeStyles } from '@material-ui/core';
import { useState } from 'react';
import { SIGNAL_HEALTH_LABELS } from './control-plane-management-labels';
import { ControlPlaneSessionObservabilityPanel } from './ControlPlaneSessionObservabilityPanel';
import { StateDimension } from './ControlPlaneStateDimension';

const useStyles = makeStyles(theme => ({
  grid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  },
  details: {
    display: 'grid',
    gap: theme.spacing(1),
  },
  detailsToggle: {
    justifySelf: 'start',
    textTransform: 'none',
    paddingLeft: 0,
    paddingRight: 0,
  },
  detailsBody: {
    paddingTop: theme.spacing(1),
  },
}));

const FINALIZATION_LABELS: Record<string, string> = {
  'not-started': 'Não iniciada',
  pending: 'Aguardando confirmação',
  completed: 'Concluída',
  aborted: 'Abortada',
  unavailable: 'Indisponível',
};

const TELEMETRY_LABELS: Record<string, string> = {
  'not-started': 'Sem espera de telemetria',
  'awaiting-stop': 'Aguardando evento final do Claude',
  flushing: 'Entregando métricas',
  confirmed: 'Métricas confirmadas',
  'timed-out': 'Concluída com métricas pendentes',
  unavailable: 'Estado de telemetria indisponível',
};

const RECOVERY_LABELS: Record<string, string> = {
  'not-started': 'Não iniciada',
  created: 'Código emitido',
  claimed: 'Leitura local autorizada',
  completed: 'Reconciliada',
  failed: 'Falhou',
  expired: 'Expirada',
  unavailable: 'Indisponível',
};

const COLLECTOR_LABELS: Record<string, string> = {
  compatible: 'Compatível',
  outdated: 'Desatualizado',
  pending: 'Entrega pendente',
  failed: 'Falhou',
  unavailable: 'Indisponível',
};

/** Collapsed by default and mounted only while expanded (RN-CONEX-04). */
export function ManagementTechnicalDetails(props: {
  projection: ControlPlaneOperationalProjection;
  stale: boolean;
}) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  return (
    <Box className={classes.details}>
      <Button
        size="small"
        color="primary"
        className={classes.detailsToggle}
        aria-expanded={open}
        aria-controls="management-technical-details"
        onClick={() => setOpen(value => !value)}
      >
        {open ? 'Ocultar detalhes técnicos' : 'Ver detalhes técnicos'}
      </Button>
      {/* Mounted only while expanded (RN-CONEX-04): nothing technical is
          announced or indexed by assistive tech until the reader asks. */}
      <Collapse in={open} unmountOnExit>
        <Box
          id="management-technical-details"
          data-testid="management-technical-details"
          className={`${classes.grid} ${classes.detailsBody}`}
        >
          <UsageReliabilityDimensions projection={props.projection} />
          <ControlPlaneSessionObservabilityPanel
            projection={props.projection}
            stale={props.stale}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

function UsageReliabilityDimensions(props: {
  projection: ControlPlaneOperationalProjection;
}) {
  const { projection } = props;
  return (
    <>
      <StateDimension
        label="Finalização das métricas"
        value={
          FINALIZATION_LABELS[projection.finalization?.state ?? 'unavailable']
        }
        detail={
          TELEMETRY_LABELS[
            projection.finalization?.telemetryState ?? 'unavailable'
          ]
        }
      />
      <StateDimension
        label="Recuperação de tokens"
        value={
          RECOVERY_LABELS[projection.usageRecovery?.state ?? 'unavailable']
        }
        detail={recoveryDetail(projection)}
      />
      <StateDimension
        label="Coletor Claude"
        value={
          COLLECTOR_LABELS[projection.usageCollector?.state ?? 'unavailable']
        }
        detail={collectorDetail(projection)}
      />
      <StateDimension
        label="Integridade sessão e origem"
        value={
          SIGNAL_HEALTH_LABELS[
            projection.signalHealth?.condition ?? 'not_observed'
          ]
        }
        detail={projection.signalHealth?.reasonCode ?? null}
      />
    </>
  );
}

function recoveryDetail(
  projection: ControlPlaneOperationalProjection,
): string | null {
  const recovery = projection.usageRecovery;
  if (!recovery) return 'Projeção legada sem estado de recuperação.';
  const counts = recovery.counts;
  if (recovery.state === 'completed') {
    return `${counts.found} encontrados; ${counts.accepted} aceitos; ${counts.replayed} reapresentados; ${counts.excluded} excluídos; ${counts.unrecoverable} não recuperáveis.`;
  }
  return recovery.lastErrorCode ?? null;
}

function collectorDetail(
  projection: ControlPlaneOperationalProjection,
): string | null {
  const collector = projection.usageCollector;
  if (!collector) return 'Projeção legada sem saúde do coletor.';
  const versions = [collector.installedVersion, collector.expectedVersion]
    .filter(Boolean)
    .join(' / ');
  const pending = `${collector.pendingCount} pendente(s)`;
  return [versions || null, pending, collector.lastErrorCode]
    .filter(Boolean)
    .join(' · ');
}
